import React, { useEffect, useState, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import { useValidation } from "../../helpers/validationHelpers";
import { useBannerDispatch } from "../../contexts/BannerContext";
import LoadingContent from "../../components/progress/LoadingContent";
import { useTrack } from "../../analytics/hooks";
import CustomRegisterNotInvitePage from "./CustomRegisterNotInvitePage";
import CustomRegisterInvitePage from "./CustomRegisterInvitePage";

const Banner = React.lazy(() =>
  import("../../components/reusable-components/Banner")
);

const commonTypographyStyles = {
  fontFamily: "Lato",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "normal",
};

const primaryTypographyStyles = {
  ...commonTypographyStyles,
  color: "var(--Primary-500, #16124C)",
};

const secondaryTypographyStyles = {
  ...commonTypographyStyles,
  color: "var(--Secondary-500, #AC5CCC)",
};

export default function CustomRegisterPage({ user, invitationToken }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const track = useTrack();
  const errorMsgFromAuth0 = params.get("error");
  const navigate = useNavigate();
  const [inputEmail, setInputEmail] = useState(user?.email || "");
  const [emailResponseError, setEmailResponseError] = useState("");
  const [botCheckPassed, setBotCheckPassed] = useState();
  const [formInputs, setFormInputs] = useState({
    jobTitle: user?.jobTitle || "",
    schoolName: user?.Accounts?.[0]?.name || "",
    schoolAdress: user?.Accounts?.[0]?.address || "",
  });
  const [newRegistrationFromCPS, setNewRegistrationFromCPS] = useState(false);
  const [newRegWithActiveInvite, setNewRegWithActiveInvite] = useState(false);
  const [accountName, setAccountName] = useState("");
  const bannerDispatch = useBannerDispatch();

  useEffect(() => {
    //clean the banner
    bannerDispatch({
      type: "changeBannerState",
      payload: {
        mainText: errorMsgFromAuth0,
        subText: "",
        tallBanner: false,
        success: false,
      },
    });
  }, []);

  const { errors, validate } = useValidation({
    jobTitle: { isNotBlank: true },
    schoolName: { isNotBlank: true },
    email: { isValidEmail: true },
  });

  const handleGoogleRegister = async () => {
    //track the register with google event
    track("user_authentication_initiated", {
      user: { id: "undefined", email: "undefined", isSuperAdmin: false },
      auth_method: "GOOGLE",
      flow_type: "REGISTER",
    });

    // Delay the redirect to give Segment time to send the event
    setTimeout(() => {
      window.location.href = invitationToken
        ? `${process.env.REACT_APP_URL}/auth/google-login?invitationToken=${invitationToken}`
        : `${process.env.REACT_APP_URL}/auth/google-login`;
    }, 300); // 300ms is usually enough, but can adjust to 500ms if needed
  };

  const handleSelectSchool = (e, val) => {
    if (!val) return;

    setFormInputs({
      ...formInputs,
      schoolName: val.headerName,
      schoolAdress: val.address,
    });
    validate("schoolName", val.headerName);
  };

  const handleSwitchToLogin = () => {
    navigate("/login", { replace: true });
  };

  const handleInputEmail = (val) => {
    //clean the email error in errors
    if (errors?.email?.isValid === false) {
      errors.email.isValid = true;
      errors.email.errorMessages = {};
    }
    setEmailResponseError("");
    setInputEmail(val);
  };

  const handleContinueWithEmail = async () => {
    //check if it is a valid email
    validate("email", inputEmail);
    if (!errors["email"].isValid) {
      return;
    }

    const inputEmailDomain = inputEmail.toLowerCase().split("@")[1];

    //check if the user is invited
    const res = await fetch("/auth/register/invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: inputEmail }),
    });
    if (res.ok) {
      const userStatus = await res.json();
      if (!userStatus.userInDB) {
        //user not in our db
        //if the email is cps email, direct to google registration page
        //otherwise show error banner since we do not allow emails to register except cps email
        if (inputEmailDomain === "cps.edu") {
          setNewRegistrationFromCPS(true);
        } else {
          bannerDispatch({
            type: "changeBannerState",
            payload: {
              mainText: "We couldn’t complete your registration",
              subText:
                "Your account isn’t eligible to register right now. If you believe this is a mistake, please contact your administrator for assistance.",
              tallBanner: true,
              success: false,
            },
          });
          return;
        }
      } else if (userStatus.registered) {
        //user in our db and finished registration
        setEmailResponseError("You have registed, please login");
      } else if (userStatus.invitationExpired) {
        //user in our db but not finished registration and with an expired invitation
        console.log(
          "user in our db but not finished registration and with an expired invitation"
        );
        navigate("/", {
          replace: true,
          state: {
            alertMsg: `You have an expired invitation. Please contact our support team to request a new invitation.`,
            alertEmail: inputEmail,
          },
        });
      } else {
        //user in our db but not finished registration and with a valid invitation
        setNewRegWithActiveInvite(true);
        setAccountName(userStatus.accountName);
      }
    } else {
      bannerDispatch({
        type: "changeBannerState",
        payload: {
          mainText: "We couldn’t complete your registration",
          subText:
            "Your account isn’t eligible to register right now. If you believe this is a mistake, please contact your administrator for assistance.",
          tallBanner: true,
          success: false,
        },
      });
      return;
    }
  };

  return (
    <Grid
      item
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#FFFFFF",
        padding: "20px",
      }}>
      <Grid container sx={{ height: "100%", width: "100%" }}>
        {/* left area */}
        <Grid item xs={12} md={6} sx={{ height: "100%" }}>
          <Grid
            container
            direction="column"
            sx={{
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              overflowY: "auto",
            }}>
            <Grid
              item
              sx={{
                height: "auto",
                width: "350px",
                padding: "87px 0 87px 0",
              }}>
              <Grid
                container
                direction="column"
                sx={{
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "20px",
                }}>
                {/* main Title */}
                <Grid
                  item
                  sx={{
                    width: "100%",
                    height: "30px",
                    marginBottom: "24px",
                  }}>
                  <Typography
                    sx={{
                      ...primaryTypographyStyles,
                      fontSize: 25,
                      fontWeight: 900,
                    }}>
                    {newRegWithActiveInvite ? (
                      <>
                        You’ve been{" "}
                        <span style={{ ...secondaryTypographyStyles }}>
                          Inno
                        </span>
                        vited!
                      </>
                    ) : (
                      <>
                        Create your{" "}
                        <span style={{ ...secondaryTypographyStyles }}>
                          Inno
                        </span>{" "}
                        Account
                      </>
                    )}
                  </Typography>
                </Grid>
                {invitationToken || user?.authInfo ? (
                  <CustomRegisterInvitePage
                    user={user}
                    inviteToken={invitationToken}
                    handleGoogleRegister={handleGoogleRegister}
                  />
                ) : (
                  <CustomRegisterNotInvitePage
                    inputEmail={inputEmail}
                    accountName={accountName}
                    handleInputEmail={handleInputEmail}
                    validate={validate}
                    errors={errors}
                    handleContinueWithEmail={handleContinueWithEmail}
                    emailResponseError={emailResponseError}
                    botCheckPassed={botCheckPassed}
                    setBotCheckPassed={setBotCheckPassed}
                    invitationToken={invitationToken}
                    handleGoogleRegister={handleGoogleRegister}
                    newRegistrationFromCPS={newRegistrationFromCPS}
                    newRegWithActiveInvite={newRegWithActiveInvite}
                  />
                )}
                {/* switch to login option */}
                {!user?.id && (
                  <Grid
                    item
                    sx={{
                      width: "100%",
                      height: "17px",
                    }}>
                    <Typography
                      sx={{
                        color: "var(--Primary-500, #16124C)",
                        fontFamily: "Lato",
                        fontSize: 14,
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "normal",
                      }}>
                      Already have an account?{" "}
                      <span
                        style={{
                          color: "var(--Tertiary-500, #00A6FF)",
                          cursor: "pointer",
                        }}
                        onClick={handleSwitchToLogin}>
                        Login here
                      </span>
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {/* right area */}
        {/* If the screen size is extra small, do not show the image */}
        <Grid
          item
          xs={6}
          className="register-page-image"
          sx={{
            height: "100%",
            display: { xs: "none", md: "block" },
          }}>
          <img
            src="/images/Product_registration-01.png"
            alt="register"
            className="registration-image"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              borderRadius: "6px",
            }}
          />
        </Grid>
      </Grid>
      <Suspense fallback={<LoadingContent />}>
        <Banner />
      </Suspense>
    </Grid>
  );
}
